import { pick } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DbIdsPerModel, useViewer } from '../../components/common/ForgeViewer';
import { useSheetViewer } from '../../components/common/SheetViewer';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { useViewerMode, ViewerMode } from 'src/components/common/viewer-mode';

// Only added the methods that were needed at time of writing, so feel free to add more if needed
export type VisibilityContext = {
  isolate: (elements: DbIdsPerModel) => void;
  hide: (elements: DbIdsPerModel) => void;
  show: (elements: DbIdsPerModel) => void;
};

// Hook for visibility of elements, shapes and sheet shapes with a single interface
// Delegating calls to the right viewer type
export const useVisibility = (): VisibilityContext => {
  const {
    visibilityManager: {
      isolate: isolateForge,
      hide: hideForge,
      show: showForge,
    },
    shapesManager: { renderedShapes },
    loadedModels,
  } = useViewer();

  const {
    shapesManager: { renderedSheetShapes: renderedSheetShapesV1 },
    visibilityManager: sheetsViewerV1VisibilityManager,
  } = useSheetViewer();

  const {
    shapesManager: { renderedSheetShapes: renderedSheetShapesV2 },
    visibilityManager: sheetsViewerV2VisibilityManager,
  } = useSheetViewerV2();

  const { viewerMode } = useViewerMode();

  const renderedSheetShapes =
    viewerMode === ViewerMode.SheetsV2
      ? renderedSheetShapesV2
      : renderedSheetShapesV1;

  // Use the appropriate visibility manager based on viewer mode
  const sheetsVisibilityManager =
    viewerMode === ViewerMode.SheetsV2
      ? sheetsViewerV2VisibilityManager
      : sheetsViewerV1VisibilityManager;

  const forgeUrns = useMemo(
    () => [...Object.keys(renderedShapes), ...Object.keys(loadedModels)],
    [renderedShapes, loadedModels]
  );

  const sheetUrns = useMemo(
    () => Object.keys(renderedSheetShapes),
    [renderedSheetShapes]
  );

  const forgeUrnsRef = useRef(forgeUrns);
  const sheetUrnsRef = useRef(sheetUrns);

  useEffect(() => {
    forgeUrnsRef.current = forgeUrns;
    sheetUrnsRef.current = sheetUrns;
  }, [forgeUrns, sheetUrns]);

  const hide = useCallback(
    (elements: DbIdsPerModel) => {
      const forgeElements = pick(elements, forgeUrnsRef.current);
      const sheetElements = pick(elements, sheetUrnsRef.current);
      if (Object.keys(forgeElements).length > 0) {
        hideForge(forgeElements);
      }
      if (Object.keys(sheetElements).length > 0) {
        sheetsVisibilityManager.hide(sheetElements);
      }
    },
    [hideForge, sheetsVisibilityManager]
  );

  const show = useCallback(
    (elements: DbIdsPerModel) => {
      const forgeElements = pick(elements, forgeUrnsRef.current);
      const sheetElements = pick(elements, sheetUrnsRef.current);
      if (Object.keys(forgeElements).length > 0) {
        showForge(forgeElements);
      }
      if (Object.keys(sheetElements).length > 0) {
        sheetsVisibilityManager.show(sheetElements);
      }
    },
    [showForge, sheetsVisibilityManager]
  );

  const isolate = useCallback(
    (elements: DbIdsPerModel) => {
      const forgeElements = pick(elements, forgeUrns);
      const sheetElements = pick(elements, sheetUrns);
      isolateForge(forgeElements);
      sheetsVisibilityManager.setIsolatedElements(sheetElements);
      sheetsVisibilityManager.setHiddenElements({});
    },
    [forgeUrns, isolateForge, sheetsVisibilityManager, sheetUrns]
  );

  return useMemo(
    () => ({
      hide,
      show,
      isolate,
    }),
    [hide, show, isolate]
  );
};
